import { hasAccess, rules } from "@trustpilot/businessapp-entitlement";

import * as Icons from "../icons";
import * as Types from "../types";

const makeMenu = ({
  sidebarRolePermissions,
  features,
  launchDarklyFlags,
  businessUnitAccessPermissions,
  reviewInsightsVisible,
  isTrustpilotEmployee,
  modules,
  reviewCountAwaitingReply,
  userRole,
}: Types.Menu): { menu: Types.PrimaryNav[] } => {
  const { hasProductReviews, hasInvitations, hasAnalytics, hasIntegrations } = sidebarRolePermissions;

  const hasAccessToRepliesData = hasAccess(
    rules,
    "REPLY_DATA_PAGE",
    features,
    modules.map((module) => module.name),
    launchDarklyFlags,
  );
  const hasPrivacyEnabled = features.includes("Invitations_PrivacyEnabled");
  const hasProductReviewRatings = features.includes("Reviews_ProductReviewsAnalytics");
  const canSeeTagsManagement = features.includes("B2B_AddRemoveTagsOnReview");
  const hasBenchmarkingAccess = features.includes("B2B_AnalyticsBenchmarking");
  const isDomainsManagementEnabled = launchDarklyFlags.includes("b2b-company-settings-link");
  const aiReplySettingsEnabled = launchDarklyFlags.includes("ai-reply-settings");
  const hasAiReviewResponsesFeature = features.includes("B2B_AIGeneratedReviewResponses");
  const hasAccessToReviewFollowUp =
    features.includes("B2B_ReviewFollowUp") && launchDarklyFlags.includes("b2b-review-follow-up");

  const hasAccessToAiReplySettings = aiReplySettingsEnabled && hasAiReviewResponsesFeature;

  const canManageServiceReviewsReference = businessUnitAccessPermissions.includes("servicereviews_manage_refnumber");

  const canSeeSAMLManagement =
    features.includes("BusinessAuthentication_SAML") &&
    businessUnitAccessPermissions.includes("businessauthentication_manage_saml_configuration");

  const canViewBillingInformation = businessUnitAccessPermissions.includes("billing_view_billinginfo");
  const canManagePublicProfile = businessUnitAccessPermissions.includes("businessunits_manage_public_profile");
  const canManageDomains =
    isDomainsManagementEnabled && businessUnitAccessPermissions.includes("businessaccounts_manage_domains");
  const canManageCategories = businessUnitAccessPermissions.includes("categories_manage_categories");
  const canManageLocations = businessUnitAccessPermissions.includes("locations_manage_locations");
  const canManageConsents = businessUnitAccessPermissions.includes("businessconsents_manage_consents");
  const canManageUsers = businessUnitAccessPermissions.includes("businessusers_manage_users");

  const hasAccessToReportingActivity = businessUnitAccessPermissions.includes("see_full_sidebar");

  const hasInvitationsDoubleOptInEnabled =
    sidebarRolePermissions.hasInvitationsDoubleOptInEnabled && launchDarklyFlags.includes("double-opt-in-opt-in");

  const hasSeoReachAccess = hasAccess(rules, "APP_SEO_REACH", features, [], launchDarklyFlags);
  const isReviewInsightsOverviewRemoved = launchDarklyFlags.includes("dev-move-review-insights-charts");

  const hasFlexWidgetEnabled = launchDarklyFlags.includes("b2b-enable-flex-widget");
  const hasAccessToAssetBuilder = features.includes("B2B_MarketingAssetBuilder");

  const shouldShowMarketInsights = hasAccess(rules, "MARKET_INSIGHTS_PAGE", features, [], launchDarklyFlags);
  const shouldShowMarketPeers = hasAccess(rules, "MARKET_PEERS_PAGE", features, [], launchDarklyFlags);
  const shouldShowMarketTopics = hasAccess(rules, "MARKET_TOPICS_PAGE", features, [], launchDarklyFlags);
  const shouldShowMarketTrends = hasAccess(rules, "MARKET_TRENDS_PAGE", features, [], launchDarklyFlags);
  const shouldShowMarketCompetitors = hasAccess(
    rules,
    "MARKET_COMPETITORS_WITH_RI_PAGE",
    features,
    [],
    launchDarklyFlags,
  );
  const shouldShowLegacyCompetitors = hasAccess(rules, "LEGACY_COMPETITORS_PAGE", features, [], launchDarklyFlags);
  const shouldShowReviewSpotlight = hasAccess(rules, "REVIEW_SPOTLIGHT_PAGE", features, [], launchDarklyFlags);
  const shouldShowSpotlightReportPage = hasAccess(rules, "SPOTLIGHT_REPORT_PAGE", features, [], launchDarklyFlags);
  const isMarketEnabled =
    shouldShowMarketInsights ||
    shouldShowMarketPeers ||
    shouldShowMarketTopics ||
    shouldShowMarketTrends ||
    shouldShowMarketCompetitors;

  const shouldShowAnalyticsExplorer = hasAccess(
    rules,
    "ANALYTICS_EXPLORER_PAGE",
    features,
    [],
    launchDarklyFlags,
    userRole,
  );

  const hasWidgetDashboardAccess = hasAccess(rules, "WIDGET_DASHBOARD_PAGE", features, [], launchDarklyFlags, userRole);
  const hasSearchDashboardAccess = hasAccess(rules, "SEARCH_DASHBOARD_PAGE", features, [], launchDarklyFlags, userRole);

  const shouldShowVisitorInsights = hasAccess(rules, "VISITOR_INSIGHTS_PAGE", features, [], launchDarklyFlags);

  const menu: Types.PrimaryNav[] = [
    {
      id: "home",
      href: "/dashboard",
      icon: <Icons.HomeOutline />,
      iconActive: <Icons.HomeFilled />,
      iconOffsetCorrection: true,
    },
    {
      id: "manageReviews",
      icon: <Icons.ManageReviews />,
      iconActive: <Icons.ReviewsFilled />,
      data: reviewCountAwaitingReply.total,
      subMenu: [
        {
          id: "serviceReviews",
          subMenu: [
            {
              id: "serviceReviewsinbox",
              href: "/reviews",
              data: reviewCountAwaitingReply.serviceReviewCount,
            },
            hasAccessToReportingActivity && {
              id: "serviceReviewsFlagging",
              href: "/reviews/reported-reviews",
            },
            shouldShowReviewSpotlight && {
              id: "reviewSpotlight",
              href: "/insights/review-spotlight",
            },
            hasAccessToReviewFollowUp && {
              id: "reviewFollowUp",
              href: "/reviews/review-follow-up",
            },
            hasAccessToAiReplySettings && {
              id: "aiReplySettings",
              href: "/reviews/ai-reply-settings",
            },
          ].filter(Boolean),
        },
        hasProductReviews && {
          id: "productReviews",
          subMenu: [
            {
              id: "productReviewsinbox",
              href: "/product-reviews",
              data: reviewCountAwaitingReply.productReviewCount,
            },
            {
              id: "productQandA",
              href: "/product-reviews/product-questions",
            },
            {
              id: "productCatalog",
              href: "/product-reviews/product-catalog",
            },
            {
              id: "productAttributes",
              href: "/product-reviews/product-attributes",
            },
            {
              id: "thirdPartyReviews",
              href: "/product-reviews/imports",
            },
            {
              id: "invitationData",
              href: "/product-reviews/analytics",
            },
            {
              id: "productReviewsSettings",
              href: "/product-reviews/product-settings",
            },
          ],
        },
      ].filter(Boolean),
    },
    hasInvitations && {
      id: "invitations",
      icon: <Icons.GetReviewsOutline />,
      iconActive: <Icons.GetReviewsFilled />,
      subMenu: [
        ...(hasPrivacyEnabled
          ? [
              {
                id: "generatedLinks",
                href: "/invitations/business-generated-links",
              },
              isTrustpilotEmployee && {
                id: "templateEditor",
                href: `/invitations/editor`,
              },
            ].filter(Boolean)
          : [
              {
                id: "invitationsOverview",
                href: "/invitations",
              },
              {
                id: "invitationMethods",
                href: "/invitations/invite-customers/invitation-methods",
              },
              {
                id: "emailTemplates",
                href: "/invitations/editor",
              },
              {
                id: "deliveryStatus",
                href: "/invitations/invitation-history",
              },
            ]),
      ],
    },
    {
      id: "shareAndPromote",
      icon: <Icons.ShareAndPromote />,
      iconActive: <Icons.ShowcaseFilled />,
      subMenu: [
        {
          id: "websiteWidgets",
          subMenu: [
            {
              id: "allWidgets",
              href: "/trustbox",
            },
            hasFlexWidgetEnabled && {
              id: "customizeWidgets",
              href: "/trustbox/customize",
            },
            {
              id: "essentialsWidgets",
              href: "/trustbox/essentials",
            },
            {
              id: "collectReviewsWidgets",
              href: "/trustbox/collect-reviews",
            },
            {
              id: "testimonialsWidgets",
              href: "/trustbox/testimonials",
            },
            {
              id: "productReviewsWidgets",
              href: "/trustbox/product-reviews",
            },
          ].filter(Boolean),
        },
        {
          id: "emailWidgets",
          subMenu: [
            {
              id: "emailWidgetsSignature",
              href: "/email-widgets/signature",
            },
            {
              id: "emailWidgetsNewsletter",
              href: "/email-widgets/newsletter",
            },
          ],
        },
        {
          id: "social",
          subMenu: [
            {
              id: "socialShare",
              href: "/share",
            },
            {
              id: "imageGenerator",
              href: "/review-image",
            },
            {
              id: "videoGenerator",
              href: "/share/review-video",
            },
            {
              id: "marketingAssets",
              href: "/marketing",
            },
            hasAccessToAssetBuilder && {
              id: "assetBuilder",
              href: "/marketing/asset-builder",
            },
          ].filter(Boolean),
        },
      ].filter(Boolean),
    },
    {
      id: "analytics",
      icon: <Icons.AnalyticsOutline />,
      iconActive: <Icons.AnalyticsFilled />,
      subMenu: hasAnalytics
        ? [
            {
              id: "performance",
              subMenu: [
                {
                  id: "analyticsOverview",
                  href: "/analytics/overview",
                },
                {
                  id: "trustscore-insights",
                  href: "/analytics/trustscoreinsights",
                },
                {
                  id: "analyticsServiceReviews",
                  href: "/analytics/reviewratings",
                },
                hasProductReviewRatings && {
                  id: "product-review-ratings",
                  href: "/analytics/productreviewratings",
                },
                hasAccessToRepliesData && {
                  id: "analyticsReplyData",
                  href: "/analytics/replies",
                },
                !hasPrivacyEnabled && {
                  id: "invitation-conversion",
                  href: "/analytics/invitationconversion",
                },
                (shouldShowLegacyCompetitors || hasBenchmarkingAccess) && {
                  id: "benchmarking",
                  href: "/benchmark",
                },
                shouldShowAnalyticsExplorer && {
                  id: "analyticsExplorer",
                  href: "/analytics/explore",
                },
              ].filter(Boolean),
            },
            reviewInsightsVisible || shouldShowSpotlightReportPage
              ? {
                  id: "reviewInsights",

                  subMenu: [
                    reviewInsightsVisible &&
                      !isReviewInsightsOverviewRemoved && {
                        id: "reviewInsightsOverview",
                        href: "/review-insights",
                      },
                    shouldShowSpotlightReportPage && {
                      id: "spotlightReport",
                      href: "/insights/spotlight-report",
                    },
                    reviewInsightsVisible && {
                      id: "reviewInsightsTopics",
                      href: "/review-insights/topics",
                    },
                    reviewInsightsVisible && {
                      id: "reviewInsightsLocations",
                      href: "/review-insights/locations",
                    },
                    reviewInsightsVisible && {
                      id: "reviewInsightsTrustScoreForecast",
                      href: "/review-insights/trustscore-forecast",
                    },
                  ].filter(Boolean),
                }
              : { id: "reviewInsights", href: "/review-insights/topics-overview" },
            shouldShowVisitorInsights && {
              id: "visitors",
              subMenu: [
                {
                  id: "visitorInsights",
                  href: "/insights/visitor",
                },
              ],
            },
            {
              id: "engagement",
              subMenu: [
                hasSearchDashboardAccess && {
                  id: "searchDashboard",
                  href: "/analytics/explore/search",
                },
                hasWidgetDashboardAccess && {
                  id: "widgetDashboard",
                  href: "/analytics/explore/widgets",
                },
                hasSeoReachAccess && {
                  id: "engagementReach",
                  href: "/reach",
                },
                hasAnalytics && {
                  id: "organicReach",
                  href: "/analytics/organicreach",
                },
              ].filter(Boolean),
            },
            isMarketEnabled && {
              id: "market",
              subMenu: [
                shouldShowMarketInsights && {
                  id: "marketInsights",
                  href: "/insights/market-insights",
                },
                shouldShowMarketPeers && {
                  id: "marketPeers",
                  href: "/insights/market-peers",
                },
                shouldShowMarketTrends && {
                  id: "marketTrends",
                  href: "/insights/market-trends",
                },
                shouldShowMarketTopics && {
                  id: "marketTopics",
                  href: "/insights/market-topics",
                },
                shouldShowMarketCompetitors && {
                  id: "marketCompetitors",
                  href: "/insights/competitors",
                },
              ].filter(Boolean),
            },
          ].filter(Boolean)
        : [
            {
              id: "engagement",
              subMenu: [
                {
                  id: "engagementReach",
                  href: "/reach",
                },
              ],
            },
          ],
    },
    hasIntegrations && {
      id: "integrations",
      icon: <Icons.AppsOutline />,
      iconActive: <Icons.AppsFilled />,
      subMenu: [
        {
          id: "ecommerce",
          href: "/integrations/ecommerce",
          // Active for paths /integrations/ecommerce/* AND /integrations/apps/salesforce BUT NOT /integrations/ecommerce/square
          customActiveCondition: `^\/integrations\/ecommerce(?!.*square$).*|\/integrations\/apps\/salesforce`,
        },
        {
          id: "payment-crm",
          href: "/integrations/payment",
          // Active for paths /integrations/payment/* AND /integrations/ecommerce/square
          customActiveCondition: `^\/integrations\/payment.*$|\/integrations\/ecommerce\/square`,
        },
        {
          id: "developers",
          href: "/integrations/developers",
          // Active for paths /integrations/developers/* AND /applications AND /webhooks AND /integrations/apps/zapier
          customActiveCondition: `^\/integrations\/developers.*$|\/applications|\/webhooks|\/integrations\/apps\/zapier`,
        },
        {
          id: "marketing",
          href: "/integrations/marketing",
          // Active for paths /integrations/marketing/*
          customActiveCondition: `^\/integrations\/marketing.*$`,
        },
        {
          id: "customerSupport",
          href: "/integrations/customersupport",
          // Active for paths /integrations/customersupport/*
          customActiveCondition: `^\/integrations\/customersupport.*$`,
        },
      ].filter(Boolean),
    },
    {
      id: "settings",
      icon: <Icons.SettingsOutline />,
      iconActive: <Icons.SettingsFilled />,
      subMenu: [
        {
          id: "settingsOverview",
          href: "/settings",
        },
        hasInvitations && {
          id: "invitationSettings",
          subMenu: [
            {
              id: "emailSettings",
              href: "/invitations/invitation-settings",
            },
            {
              id: "timeDelivery",
              href: "/invitations/invitation-settings/timing-and-frequency",
            },
            {
              id: "legalNotice",
              href: "/invitations/invitation-settings/impressum",
            },
            {
              id: "consumerPrivacy",
              href: "/invitations/invitation-settings/privacy",
            },
            hasInvitationsDoubleOptInEnabled && {
              id: "invitationsDoubleOptIn",
              href: "/invitations/invitation-settings/double-opt-in",
            },
          ].filter(Boolean),
        },
        (canManageDomains ||
          canManageUsers ||
          canManageConsents ||
          canViewBillingInformation ||
          canSeeSAMLManagement) && {
          id: "businessSettings",
          subMenu: [
            canManageUsers && {
              id: "users",
              href: "/usermanagement",
            },
            canManageConsents && {
              id: "dataConsent",
              href: "/account/permissions",
            },
            canSeeSAMLManagement && {
              id: "samlSettings",
              href: "/saml",
            },
          ].filter(Boolean),
        },
        (canManagePublicProfile || canManageCategories || canManageLocations || canManageServiceReviewsReference) && {
          id: "publicProfileSettings",
          subMenu: [
            canManagePublicProfile && {
              id: "profilePage",
              href: "/company-settings",
            },
            canManageCategories && {
              id: "categories",
              href: "/settings/categories",
            },
            canManageLocations && {
              id: "publicProfileLocations",
              href: "/locations/summary",
            },
            canManageServiceReviewsReference && {
              id: "referenceNumber",
              href: "/settings/reference",
            },
            canSeeTagsManagement && {
              id: "publicProfiletTags",
              href: "/tags",
            },
          ].filter(Boolean),
        },
        {
          id: "personalSettings",
          subMenu: [
            {
              id: "myDetails",
              href: "/account/settings",
            },
            {
              id: "emailNotifications",
              href: "/settings/notifications",
            },
          ],
        },
      ].filter(Boolean),
    },
    {
      id: "help",
      href: "/settings/contact",
      hideOnLargerScreens: true,
      icon: <Icons.HelpOutline />,
      iconActive: <Icons.HelpFilled />,
    },
  ].filter(Boolean);

  return {
    menu,
  };
};

export default makeMenu;
